/**
 * AdvertiserBillingPage — Advertiser billing roster.
 *
 * Ported from pre-nuke legacy `apps/dash-legacy/src/routes/_authed/settings/billing.tsx`.
 * Modernized: dropped sonner/lucide-react/shadcn imports; plain Tailwind only;
 * light theme; emerald->sky gradient H1; zinc borders; rounded-md/lg.
 *
 * Columns: Advertiser | Current balance | Billing cycle | Last invoice | Status.
 * Drill-through: row click opens invoice history drawer.
 * Generate Invoice: button per row with confirm dialog.
 * Balance badge: emerald when >= 0, red when < 0.
 */
import { useState, type FormEvent } from "react";
import {
	useAdvertiserBillingList,
	useAdvertiserBillingCreate,
	useAdvertiserInvoices,
	useGenerateInvoice,
} from "./hooks";
import type {
	AdvertiserBillingRow,
	AdvertiserStatus,
	BillingCycle,
	InvoiceStatus,
} from "./api";

const CARD = "rounded-lg border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const SELECT = INPUT;
const BTN =
	"rounded-md border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_OUTLINE =
	"rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-zinc-500";
const TD = "px-3 py-3 text-sm text-zinc-900";

function formatCents(cents: number | null | undefined): string {
	if (cents === null || cents === undefined) return "—";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const dollars = Math.floor(abs / 100);
	const rem = abs % 100;
	return `${sign}$${dollars.toLocaleString()}.${rem.toString().padStart(2, "0")}`;
}

function formatDate(ts: number | null | undefined): string {
	if (!ts) return "—";
	const d = new Date(ts);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleDateString();
}

function cycleLabel(c: BillingCycle): string {
	if (c === "monthly") return "Monthly";
	if (c === "weekly") return "Weekly";
	return "NET30";
}

function BalanceBadge({ cents }: { cents: number }) {
	const positive = cents >= 0;
	const cls = positive
		? "bg-emerald-50 text-emerald-700 border-emerald-200"
		: "bg-red-50 text-red-700 border-red-200";
	return (
		<span
			className={`inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-xs ${cls}`}
			data-testid="advertiser-billing-balance-badge"
		>
			{formatCents(cents)}
		</span>
	);
}

function StatusPill({ status }: { status: AdvertiserStatus }) {
	const map: Record<AdvertiserStatus, string> = {
		active: "bg-emerald-50 text-emerald-700 border-emerald-200",
		past_due: "bg-amber-50 text-amber-700 border-amber-200",
		suspended: "bg-red-50 text-red-700 border-red-200",
		closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
	};
	return (
		<span
			className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium capitalize ${map[status]}`}
		>
			{status.replace("_", " ")}
		</span>
	);
}

function InvoiceStatusPill({ status }: { status: InvoiceStatus }) {
	const map: Record<InvoiceStatus, string> = {
		paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
		open: "bg-sky-50 text-sky-700 border-sky-200",
		draft: "bg-zinc-100 text-zinc-600 border-zinc-200",
		void: "bg-red-50 text-red-700 border-red-200",
		uncollectible: "bg-red-50 text-red-700 border-red-200",
	};
	return (
		<span
			className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium capitalize ${map[status]}`}
		>
			{status}
		</span>
	);
}

function GenerateConfirm({
	advertiser,
	onClose,
}: {
	advertiser: AdvertiserBillingRow;
	onClose: () => void;
}) {
	const gen = useGenerateInvoice();

	const handleConfirm = () => {
		gen.mutate(
			{ advertiserId: advertiser.id },
			{ onSuccess: () => onClose() },
		);
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
			role="dialog"
			aria-modal="true"
			data-testid="advertiser-billing-generate-confirm"
		>
			<div className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-5 shadow-lg">
				<h3 className="text-base font-semibold text-zinc-900">
					Generate invoice
				</h3>
				<p className="mt-2 text-sm text-zinc-600">
					Create a new invoice for{" "}
					<span className="font-semibold text-zinc-900">
						{advertiser.advertiser_name}
					</span>
					? Current balance is {formatCents(advertiser.current_balance_cents)}.
					This cannot be undone.
				</p>
				{gen.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{gen.error.message}
					</p>
				) : null}
				<div className="mt-4 flex justify-end gap-2">
					<button
						type="button"
						className={BTN_OUTLINE}
						onClick={onClose}
						disabled={gen.isPending}
						data-testid="advertiser-billing-generate-cancel"
					>
						Cancel
					</button>
					<button
						type="button"
						className={BTN}
						onClick={handleConfirm}
						disabled={gen.isPending}
						data-testid="advertiser-billing-generate-confirm"
					>
						{gen.isPending ? "Generating." : "Confirm & generate"}
					</button>
				</div>
			</div>
		</div>
	);
}

function InvoiceDrawer({
	advertiser,
	onClose,
}: {
	advertiser: AdvertiserBillingRow;
	onClose: () => void;
}) {
	const q = useAdvertiserInvoices(advertiser.id);
	const invoices = q.data?.items ?? [];

	return (
		<div
			className="fixed inset-0 z-40 flex justify-end bg-zinc-900/30"
			role="dialog"
			aria-modal="true"
			data-testid="advertiser-billing-invoice-drawer"
		>
			<div className="flex h-full w-full max-w-xl flex-col border-l border-zinc-200 bg-white shadow-xl">
				<div className="flex items-start justify-between border-b border-zinc-200 px-5 py-4">
					<div>
						<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							Invoice history
						</p>
						<h3 className="mt-0.5 text-base font-semibold text-zinc-900">
							{advertiser.advertiser_name}
						</h3>
					</div>
					<button
						type="button"
						className={BTN_GHOST}
						onClick={onClose}
						data-testid="advertiser-billing-drawer-close"
					>
						Close
					</button>
				</div>
				<div className="flex-1 overflow-y-auto p-5">
					{q.isLoading ? (
						<p className="text-sm text-zinc-500">Loading invoices.</p>
					) : q.error ? (
						<p className="text-sm text-red-600" role="alert">
							{q.error.message}
						</p>
					) : invoices.length === 0 ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="advertiser-billing-invoices-empty"
						>
							No invoices yet.
						</p>
					) : (
						<table
							className="w-full border-collapse"
							data-testid="advertiser-billing-invoice-table"
						>
							<thead>
								<tr className="border-b border-zinc-200">
									<th className={TH}>Number</th>
									<th className={TH}>Date</th>
									<th className={TH}>Amount</th>
									<th className={TH}>Status</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{invoices.map((inv) => (
									<tr key={inv.id} className="hover:bg-zinc-50">
										<td className={`${TD} font-mono text-xs`}>
											{inv.number}
										</td>
										<td className={`${TD} text-xs text-zinc-500`}>
											{formatDate(inv.created_at)}
										</td>
										<td className={`${TD} font-mono`}>
											{formatCents(inv.amount_cents)}
										</td>
										<td className={TD}>
											<InvoiceStatusPill status={inv.status} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);
}

export function AdvertiserBillingPage() {
	const listQuery = useAdvertiserBillingList();
	const createMut = useAdvertiserBillingCreate();

	const [advName, setAdvName] = useState("");
	const [cycle, setCycle] = useState<BillingCycle>("monthly");

	const [drawerFor, setDrawerFor] = useState<AdvertiserBillingRow | null>(null);
	const [confirmFor, setConfirmFor] = useState<AdvertiserBillingRow | null>(
		null,
	);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!advName.trim()) return;
		createMut.mutate(
			{ advertiser_name: advName.trim(), billing_cycle: cycle },
			{
				onSuccess: () => {
					setAdvName("");
					setCycle("monthly");
				},
			},
		);
	};

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="advertiser-billing-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					advertiser-billing
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Advertiser Billing
				</h1>
				<p className="text-sm text-zinc-500">
					Balances, billing cycles, invoices, and account status per advertiser.
				</p>
			</header>

			<section className={CARD} aria-label="Create advertiser">
				<h2 className="text-base font-semibold text-zinc-900">
					Add advertiser
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_auto]"
					data-testid="advertiser-billing-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Advertiser name"
						value={advName}
						onChange={(e) => setAdvName(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="advertiser-billing-name-input"
					/>
					<select
						className={SELECT}
						value={cycle}
						onChange={(e) =>
							setCycle(e.currentTarget.value as BillingCycle)
						}
						disabled={createMut.isPending}
						data-testid="advertiser-billing-cycle-input"
					>
						<option value="monthly">Monthly</option>
						<option value="weekly">Weekly</option>
						<option value="net30">NET30</option>
					</select>
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending || !advName.trim()}
						data-testid="advertiser-billing-submit"
					>
						{createMut.isPending ? "Saving." : "Add advertiser"}
					</button>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Advertiser roster">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Advertisers
					</h2>
					<span className="text-xs text-zinc-500">
						{items.length} {items.length === 1 ? "advertiser" : "advertisers"}
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="advertiser-billing-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-4 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="advertiser-billing-empty"
					>
						No advertisers yet.
					</p>
				) : (
					<div
						className="mt-4 overflow-x-auto"
						data-testid="advertiser-billing-list"
					>
						<table className="w-full border-collapse">
							<thead>
								<tr className="border-b border-zinc-200">
									<th className={TH}>Advertiser</th>
									<th className={TH}>Current balance</th>
									<th className={TH}>Billing cycle</th>
									<th className={TH}>Last invoice</th>
									<th className={TH}>Status</th>
									<th className={`${TH} text-right`}>Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{items.map((row) => (
									<tr
										key={row.id}
										className="cursor-pointer hover:bg-zinc-50"
										onClick={() => setDrawerFor(row)}
										data-testid={`advertiser-billing-row-${row.id}`}
									>
										<td className={TD}>
											<div className="font-medium">
												{row.advertiser_name}
											</div>
											<div className="font-mono text-xs text-zinc-400">
												{row.id}
											</div>
										</td>
										<td className={TD}>
											<BalanceBadge cents={row.current_balance_cents} />
										</td>
										<td className={`${TD} text-zinc-600`}>
											{cycleLabel(row.billing_cycle)}
										</td>
										<td className={`${TD} text-zinc-600`}>
											<div>{formatDate(row.last_invoice_at)}</div>
											<div className="font-mono text-xs text-zinc-400">
												{formatCents(row.last_invoice_amount_cents)}
											</div>
										</td>
										<td className={TD}>
											<StatusPill status={row.status} />
										</td>
										<td
											className={`${TD} text-right`}
											onClick={(e) => e.stopPropagation()}
										>
											<button
												type="button"
												className={BTN_OUTLINE}
												onClick={() => setConfirmFor(row)}
												data-testid={`advertiser-billing-generate-${row.id}`}
											>
												Generate invoice
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{drawerFor ? (
				<InvoiceDrawer
					advertiser={drawerFor}
					onClose={() => setDrawerFor(null)}
				/>
			) : null}
			{confirmFor ? (
				<GenerateConfirm
					advertiser={confirmFor}
					onClose={() => setConfirmFor(null)}
				/>
			) : null}
		</main>
	);
}

export default AdvertiserBillingPage;
